/**
 * Sheet — the bottom sheet on mobile, a centred dialog on desktop.
 *
 * Directive §10: ~0.25s animation, matching the screen fade.
 * Directive §6: the sheet clears the home-indicator inset on mobile.
 *
 * Focus is trapped while open and restored on close. Without that, a keyboard
 * or screen-reader user tabs straight out of the sheet into the page behind it,
 * which for the Call Log and Field Outcome sheets means silently interacting
 * with the wrong record.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** Rendered in the sticky footer. */
  actions?: ReactNode;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
  'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Sheet({ open, title, onClose, actions, children }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    restoreTo.current = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>('[data-initial-focus]')
      ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    // Prevent the page behind from scrolling under the sheet.
    const bodyWasLocked = document.body.classList.contains('overflow-hidden');
    document.body.classList.add('overflow-hidden');

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;

      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];

      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (!bodyWasLocked) document.body.classList.remove('overflow-hidden');
      restoreTo.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="dialog-backdrop animate-screen-in" onClick={onClose} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="dialog animate-sheet-up max-h-[90vh] flex flex-col
                   pb-[env(safe-area-inset-bottom)] desktop:pb-0"
      >
        <div className="flex items-start gap-2">
          <h3 className="dialog-title flex-1">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="btn btn-ghost mt-3 mr-2 min-h-touch w-touch p-0"
          >
            <XMarkIcon className="w-6 h-6" aria-hidden />
          </button>
        </div>

        <div className="dialog-body overflow-y-auto flex-1">{children}</div>

        {actions && (
          <div className="dialog-actions border-t border-neutral-300 bg-bg">{actions}</div>
        )}
      </div>
    </>
  );
}

/**
 * Transient toast. §10 keeps these at ~2.6 seconds.
 * `role="status"` rather than `alert` so it does not interrupt a screen reader
 * mid-sentence for a routine confirmation.
 */
export function Toast({ message, onDone }: { message: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [message, onDone]);

  if (!message) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed z-50 left-1/2 -translate-x-1/2
                 bottom-[calc(96px+env(safe-area-inset-bottom))] desktop:bottom-8
                 bg-neutral-900 text-white px-4 py-2 rounded-lg shadow-lg
                 text-base animate-screen-in"
    >
      {message}
    </div>
  );
}
